import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { Box, Typography, Button } from '@mui/material';
import { AddCircleRounded } from '@mui/icons-material';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import { useTranslation } from 'react-i18next';
import route from '@src/constants/route';
import FirstVoiceBoxImg from '@src/assets/images/create-first-voice-box.svg';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import actions from '@src/redux/actions';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import { MOBILE_BREAKPOINT } from '@src/constants';
import {
  AIVOICE_TTS_APP_STORE_URL,
  AIVOICE_TTS_DEEPLINK,
  AIVOICE_TTS_GOOGLE_PLAY_URL,
} from '@src/constants/mobileApp';
import apis from '@src/apis';
import { TIME_EXPIRE_QR_VOICE_CLONING } from '@src/constants/voiceCloning';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import { StyledFirstVoice } from './index.style';
import QRDialog from './QRDialog';

const FirstVoice = () => {
  const { keycloak } = useCustomKeycloak();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpireDate, setQrExpireDate] = useState('');
  const [qrRedirectUrl, setQRRedirectUrl] = useState('');

  const { getFeatureValue } = useFeatureFlags();
  const loginByQR =
    getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) || validateUserPackageCanUseVoiceCloning(ttsUser);

  const isNewQRFlow = getFeatureValue(FEATURE_KEYS.NEW_QR_FLOW, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const handleClickCreateInMobile = () => {
    history.push(route.VOICE_CLONING_CREATE);
  };

  const generateQRCodeUrl = async (loginCode) => {
    if (isNewQRFlow) {
      const screenApp = 'voiceCloning';
      return `${LANDING_PAGE_URL}/apps/aivoice-tts?screen=${screenApp}&loginCode=${loginCode}`;
    }

    let response;
    try {
      response = await apis.shortlink.generateShortlink({
        webAppUrl: `${CRM_URL}/studio/home?loginCode=${loginCode}`,
        deeplink: `${AIVOICE_TTS_DEEPLINK.LOGIN}?loginCode=${loginCode}`,
        appStoreUrl: AIVOICE_TTS_APP_STORE_URL,
        googlePlayUrl: AIVOICE_TTS_GOOGLE_PLAY_URL,
        ttl: TIME_EXPIRE_QR_VOICE_CLONING,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('errorGenerateQRCode', error);
    }
    if (!response?.status || !response?.result?.shortlink) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return null;
    }
    return response?.result?.shortlink;
  };

  const handleOpenQR = async (redirectUrl) => {
    setQRRedirectUrl(redirectUrl);

    const loginCode = await keycloak.generateLoginCode(redirectUrl);
    if (!loginCode) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return;
    }
    const url = await generateQRCodeUrl(loginCode);
    if (!url) return;

    setQrExpireDate(moment().add(TIME_EXPIRE_QR_VOICE_CLONING, 'milliseconds'));
    setQrCodeUrl(url);
  };

  const handleClickCreate = async () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      handleClickCreateInMobile();
      return;
    }
    const redirectUrl = `${CRM_URL}${route.VOICE_CLONING_CREATE}`;
    if (loginByQR) await handleOpenQR(redirectUrl);
    setOpenQRDialog(true);
  };

  const handleCloseQRDialog = () => {
    setOpenQRDialog(false);
  };

  return (
    <StyledFirstVoice>
      <Box className="box">
        <img src={FirstVoiceBoxImg} alt="first-voice-box" className="image" />
        <Typography className="text-empty-list">
          {t('emptyVoiceList')}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          className="button"
          onClick={handleClickCreate}
        >
          <AddCircleRounded className="icon" />
          {t('generateVoice')}
        </Button>
      </Box>
      <QRDialog
        open={openQRDialog}
        urlToShow={qrCodeUrl}
        qrExpireDate={qrExpireDate}
        qrRedirectUrl={qrRedirectUrl}
        onClose={handleCloseQRDialog}
        refreshQRCode={handleOpenQR}
      />
    </StyledFirstVoice>
  );
};

export default FirstVoice;
